<div class="kpi-card" [style.border-color]="lastMonthEvaluationStyle().color">
  <!-- Header -->
  <div class="kpi-header">
    <div class="kpi-header-left">
      <div class="kpi-title">
        <mat-icon class="kpi-icon">{{ kpiIcon() }}</mat-icon>
        <h3>{{ kpiData().production_key_performance_indicator.name }}</h3>
      </div>

      <div class="kpi-badge-container">
        <div
          class="kpi-badge"
          [style.background-color]="lastMonthEvaluationStyle().bgColor"
          [style.color]="lastMonthEvaluationStyle().color"
        >
          {{ lastMonthEvaluationStyle().label }}
        </div>
        @if (shouldShowConsecutiveMediumTooltip()) {
          <mat-icon
            class="info-icon"
            [matTooltip]="consecutiveMediumTooltipMessage()"
            matTooltipPosition="above"
            matTooltipShowDelay="300"
            matTooltipClass="kpi-tooltip"
            >info</mat-icon
          >
        }
      </div>
    </div>
    <div class="kpi-description">
      <p>{{ kpiData().production_key_performance_indicator.description }}</p>
    </div>
  </div>

  @if (hasException()) {
    <div class="kpi-not-calculable">
      <div class="not-calculable-title">
        <span class="not-calculable-label">Evaluación con excepción</span>
      </div>
      <p class="not-calculable-message">
        {{ exceptionMessage() }}
      </p>
    </div>
  } @else if (isNotCalculable()) {
    <div class="kpi-not-calculable">
      <div class="not-calculable-title">
        <span class="not-calculable-label">Mes no calculable</span>
        <span class="not-calculable-period">{{
          $safeNavigationMigration(currentMonthEvaluation()?.month_end_date) | date: 'LLLL y' : '' : 'es-ES'
        }}</span>
      </div>
      <p class="not-calculable-message">No hay suficientes datos evaluables en este periodo para calcular el rendimiento.</p>
    </div>
  } @else {
    <!-- Value -->
    <div class="kpi-value">
      <span class="value">{{ lastMonthValue() | number: '1.0-0' }}{{ valueSuffix() }}</span>
      <span class="period">{{ $safeNavigationMigration(currentMonthEvaluation()?.month_end_date) | date: 'LLLL y' : '' : 'es-ES' }}</span>
      @if (!usesAbsoluteThresholds()) {
        <mat-icon
          class="info-icon"
          [matTooltip]="thresholdTooltipText()"
          matTooltipPosition="above"
          matTooltipShowDelay="300"
          matTooltipClass="kpi-tooltip"
        >
          info
        </mat-icon>
      }
    </div>

    <!-- Progress Bar -->
    @if (!usesAbsoluteThresholds()) {
      <div class="progress-bar-container">
        <div class="progress-bar-track">
          <div class="progress-bar-fill" [style.width.%]="progressValuePosition()" [style.background-color]="progressBarColor()"></div>

          <div
            class="threshold-line"
            [style.left.%]="mediumThresholdPosition()"
            matTooltip="Aceptable: {{ mediumThreshold() | number: '1.0-0' }}{{ valueSuffix() }}"
            matTooltipPosition="above"
            matTooltipShowDelay="200"
            matTooltipClass="kpi-tooltip"
          ></div>

          @if (hasGoodThreshold()) {
            <div
              class="threshold-line"
              [style.left.%]="goodThresholdPosition()"
              matTooltip="Excelente: {{ goodThreshold() | number: '1.0-0' }}{{ valueSuffix() }}"
              matTooltipPosition="above"
              matTooltipShowDelay="200"
              matTooltipClass="kpi-tooltip"
            ></div>
          }
        </div>
      </div>
    }
  }

  <!-- History -->
  <div class="kpi-history">
    <p class="history-title">Últimos 6 meses:</p>
    <div class="history-months">
      @for (month of historyDataWithStyle(); track $index) {
        <div class="history-month-item">
          @if (month) {
            <div
              class="history-month-box"
              [style.background-color]="month.style.color"
              [title]="(month.percentage ?? 0) + valueSuffix()"
            ></div>
            <span class="history-month-label">{{ month.month_end_date | date: 'MMM yy' : '' : 'es-ES' }}</span>
          } @else {
            <div class="history-month-box empty-state"></div>
            <span class="history-month-label empty-label">-</span>
          }
        </div>
      }
    </div>
  </div>

  <!-- Componente hijo específico por tipo de KPI -->
  @if (isRegisteredWorkCompliance()) {
    <app-registered-work-compliance-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isMeetingAttendance()) {
    <app-meeting-attendance-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isCompanyProductDailyStandupCompliance()) {
    <app-company-product-daily-standup-compliance-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isCompanyProductDeploysFrequency()) {
    <app-company-product-deploys-frequency-details [kpiData]="kpiData()" />
  }

  @if (isUptimeMonitors()) {
    <app-uptime-monitors-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isCompanyProductExpectedVsRegisteredWork()) {
    <app-company-product-expected-vs-registered-work-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isCompanyProductPhaseTimeCompliance()) {
    <app-company-product-phase-time-compliance-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isScheduleAndRemoteWorkCompliance()) {
    <app-schedule-and-remote-work-compliance-details [current_month_evaluation]="currentMonthEvaluation()" />
  }

  @if (isSharePresenceWithColleagues()) {
    <app-share-presence-with-colleagues-details
      [currentMonthEvaluation]="currentMonthEvaluation()"
      [goodThreshold]="goodThreshold() ?? 0"
      [mediumThreshold]="mediumThreshold()"
    />
  }

  @if (isConfiguredApplicationsKpi()) {
    <app-configured-applications-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isSprintReviewExecution()) {
    <app-sprint-review-execution-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isIssuesRatioCheck()) {
    <app-issues-ratio-check-details [kpiData]="kpiData()" />
  }

  @if (isTasksReviewedByColleague()) {
    <app-tasks-reviewed-by-colleague-details [kpiData]="kpiData()" />
  }

  @if (isProductTrioTaskCreation()) {
    <app-product-trio-task-creation-details
      [kpiData]="kpiData()"
      [optimalThreshold]="goodThreshold() ?? 0"
      [acceptableThreshold]="mediumThreshold()"
    />
  }

  @if (isSprintPlanningExecution()) {
    <app-sprint-planning-execution-details [currentMonthEvaluation]="currentMonthEvaluation()" />
  }

  @if (isHoldedConfigured()) {
    <app-holded-configured-details [kpiData]="kpiData()" />
  }
</div>
